import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  Box, Typography, Container, TextField, CircularProgress,
  Button, Select, MenuItem, FormControl, Dialog, DialogTitle,
  DialogContent, IconButton, Avatar, Autocomplete, Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import FilterListIcon from "@mui/icons-material/FilterList";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PersonIcon from "@mui/icons-material/Person";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import HowToVoteIcon from "@mui/icons-material/HowToVote";
import GroupsIcon from "@mui/icons-material/Groups";
import BusinessIcon from "@mui/icons-material/Business";
import VerifiedIcon from "@mui/icons-material/Verified";
import RefreshIcon from "@mui/icons-material/Refresh";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import { useNavigate, useLocation, useMatch } from "react-router-dom";
import { useUser } from "../context/UserContext";
import { theme } from "../theme/colors";
import {
  searchUsers, searchCongressMembers, fetchFECCandidates,
  followUser, unfollowUser,
  mapApiUserToSummary, mapCongressMemberToSummary, mapFECCandidateToDisplay,
  FILTER_OPTIONS, VISIBLE_FILTER_OPTIONS, LOGIN_REQUIRED_FILTERS, CIVIX_USER_ACCOUNT_TYPE, ACCOUNT_TYPE_LABELS,
  getOrganizationSubtypeLabel,
  US_STATES, STATE_DISTRICT_MAP, getDistrictOptions, normalizeDistrictForApi,
  PARTY_COLORS, PARTY_LABELS,
  buildFECCandidateListParams,
  DEFAULT_CANDIDATE_OFFICE,
  getVisibleCandidateOfficeOptions,
  getCandidateOfficeLabel,
  getDiscoverSearchPlaceholder,
  normalizeCandidateOffice,
  shouldShowDistrictForCandidateOffice,
  shouldSuggestStateFilterForCandidates,
} from "../utils/discoverApi";
import CandidateResultCard from "../components/People/CandidateResultCard";
import CandidateOfficeFilter from "../components/People/CandidateOfficeFilter";
import CallsLeaderboard from "../components/People/CallsLeaderboard";

const STATE_OPTIONS = [{ code: "", name: "All States" }, ...US_STATES];

// ============================================================================
// USER / CONGRESS RESULT CARD
// ============================================================================
function UserResultCard({ user, onFollowToggle, isLoggedIn, onCardClick, showAccountTypeBadge }) {
  const [isFollowing, setIsFollowing] = useState(user.viewerIsFollowing);
  const [isLoading, setIsLoading] = useState(false);
  const [followerCount, setFollowerCount] = useState(user.followerCount || 0);
  const [showNoAccountModal, setShowNoAccountModal] = useState(false);

  const hasAccount = user.hasCivixThreadAccount !== false;
  const initials = user.displayName.split(" ").map((n) => n[0]).join("").toUpperCase().slice(0, 2);
  const isOrg = ["organization", "non_profit"].includes(user.accountType);

  const handleFollowPress = async (e) => {
    e.stopPropagation();
    if (!isLoggedIn) return;
    if (!hasAccount) { setShowNoAccountModal(true); return; }
    setIsLoading(true);
    try {
      const userIdToFollow = user.civixThreadUserId || user.id;
      await onFollowToggle(userIdToFollow, !isFollowing);
      const next = !isFollowing;
      setIsFollowing(next);
      setFollowerCount((prev) => next ? prev + 1 : Math.max(0, prev - 1));
    } catch { /* handled upstream */ } finally { setIsLoading(false); }
  };

  return (
    <>
      <Box
        onClick={() => onCardClick(user)}
        sx={{
          display: "flex", alignItems: "flex-start", gap: 2, p: 2.5,
          cursor: "pointer", transition: "background 0.15s",
          "&:hover": { bgcolor: "#f8fafc" },
          borderBottom: "1px solid #f1f5f9",
        }}
      >
        <Avatar
          src={user.avatarUrl || undefined}
          sx={{ width: 48, height: 48, bgcolor: `${theme.primary}20`, color: theme.primary, fontWeight: 700, fontSize: 16 }}
        >
          {initials}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 15, color: "#0f172a", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {user.displayName}
            </Typography>
            {user.isVerified && <VerifiedIcon sx={{ fontSize: 16, color: theme.primary }} />}
            {isOrg && !user.isVerified && (
              <Chip label="Unverified" size="small" sx={{ height: 20, fontSize: 10, bgcolor: "#f1f5f9", color: "#6b7280" }} />
            )}
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 0.5 }}>
            {showAccountTypeBadge && ACCOUNT_TYPE_LABELS[user.accountType] && (
              <Chip
                label={ACCOUNT_TYPE_LABELS[user.accountType]}
                size="small"
                sx={{ minHeight: 22, fontSize: 11, bgcolor: "#dbeafe", color: "#1e3a8a", fontWeight: 700 }}
              />
            )}
            {isOrg && user.organizationSubtype && (
              <Chip label={getOrganizationSubtypeLabel(user.organizationSubtype)} size="small" icon={<BusinessIcon sx={{ fontSize: 12 }} />}
                sx={{ height: 22, fontSize: 11, bgcolor: `${theme.primary}15`, color: theme.primary, "& .MuiChip-icon": { color: theme.primary } }} />
            )}
            {!isOrg && user.districtLabel && (
              <Typography sx={{ fontSize: 13, color: "#000" }}>{user.districtLabel}</Typography>
            )}
            {user.party && (
              <Chip label={PARTY_LABELS[user.party] || user.party} size="small"
                sx={{ height: 22, fontSize: 11, bgcolor: `${PARTY_COLORS[user.party] || "#94a3b8"}20`, color: "#000", fontWeight: 600 }} />
            )}
            {hasAccount && followerCount > 0 && (
              <Typography sx={{ fontSize: 13, color: "#64748b" }}>
                {followerCount} {followerCount === 1 ? "follower" : "followers"}
              </Typography>
            )}
          </Box>

          {user.bio && (
            <Typography sx={{ fontSize: 14, color: "#334155", lineHeight: 1.4, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
              {user.bio}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexShrink: 0, alignSelf: "center" }}>
          {hasAccount && (
            <Button
              variant={isFollowing ? "outlined" : "contained"}
              size="small"
              onClick={handleFollowPress}
              disabled={isLoading || !isLoggedIn}
              sx={{
                textTransform: "none", fontWeight: 700, borderRadius: "8px", minWidth: 90, height: 36,
                ...(isFollowing
                  ? { borderColor: "#e2e8f0", color: "#334155", bgcolor: "#f8fafc", "&:hover": { bgcolor: "#f1f5f9", borderColor: "#cbd5e1" } }
                  : { background: theme.gradient, color: "#fff", "&:hover": { opacity: 0.9, background: theme.gradient } }
                ),
              }}
            >
              {isLoading ? <CircularProgress size={18} color="inherit" /> : isFollowing ? "Following" : "Follow"}
            </Button>
          )}
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: 28, height: 28, borderRadius: "50%", background: theme.gradient }}>
            <ChevronRightIcon sx={{ fontSize: 18, color: "#fff" }} />
          </Box>
        </Box>
      </Box>

      <Dialog open={showNoAccountModal} onClose={() => setShowNoAccountModal(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "16px", textAlign: "center", p: 1 } }}>
        <DialogContent>
          <Typography sx={{ fontWeight: 700, fontSize: 18, mb: 1 }}>No CivixThread Account</Typography>
          <Typography sx={{ fontSize: 15, color: "#334155", mb: 1 }}>{user.displayName} hasn't joined CivixThread yet.</Typography>
          <Typography sx={{ fontSize: 14, color: "#64748b", mb: 3, lineHeight: 1.5 }}>
            When they create an account, you'll be able to follow them and see their posts.
          </Typography>
          <Button variant="contained" onClick={() => setShowNoAccountModal(false)}
            sx={{ borderRadius: "20px", px: 4, fontWeight: 700, textTransform: "none", background: theme.gradient, "&:hover": { opacity: 0.9, background: theme.gradient } }}>
            Got it
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ============================================================================
// MAIN DISCOVER PAGE
// ============================================================================
export default function DiscoverPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const isCandidatesRoute = Boolean(useMatch("/candidates"));
  const { user, userState } = useUser() || {};
  const isLoggedIn = !!user;

  const initialParams = location.state || {};
  const defaultFilterId = initialParams.filterType
    || (isCandidatesRoute ? "candidates" : "representatives");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState(defaultFilterId);
  const [selectedAccountTypeName, setSelectedAccountTypeName] = useState(
    initialParams.accountType || FILTER_OPTIONS.find(f => f.id === defaultFilterId)?.accountTypeName || "representative"
  );
  const [selectedState, setSelectedState] = useState(initialParams.state || userState || null);
  const [selectedDistrict, setSelectedDistrict] = useState(initialParams.district || null);
  const [selectedCandidateOffice, setSelectedCandidateOffice] = useState(
    normalizeCandidateOffice(initialParams.candidateOffice || DEFAULT_CANDIDATE_OFFICE)
  );
  const [civixAccountOnly, setCivixAccountOnly] = useState(false);

  const visibleCandidateOfficeOptions = useMemo(() => getVisibleCandidateOfficeOptions(), []);

  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const PAGE_SIZE = 30;
  const loadingRef = useRef(false);
  const prevStateRef = useRef(selectedState);
  const allCongressResultsRef = useRef([]);

  const [showFilterModal, setShowFilterModal] = useState(false);
  const [activeTab, setActiveTab] = useState("find");

  const isCandidateBrowse = selectedAccountTypeName === "candidate";
  const candidateLocationRequired =
    isCandidateBrowse && shouldSuggestStateFilterForCandidates(selectedCandidateOffice);

  // Auth-gate: reset hidden "all" or logged-out gated filters to representatives
  useEffect(() => {
    if (activeFilter === "all") {
      setActiveFilter("representatives");
      setSelectedAccountTypeName("representative");
      return;
    }
    if (!isLoggedIn && LOGIN_REQUIRED_FILTERS.includes(activeFilter)) {
      setActiveFilter("representatives");
      setSelectedAccountTypeName("representative");
      setSelectedState(null);
      setSelectedDistrict(null);
      setCivixAccountOnly(false);
    }
  }, [isLoggedIn, activeFilter]);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(searchQuery), 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Logged-in users: default House/Senate candidate browse to their state
  useEffect(() => {
    if (!candidateLocationRequired || selectedState || !userState) return;
    setSelectedState(userState);
  }, [candidateLocationRequired, selectedState, userState]);

  // Reset district when state changes
  useEffect(() => {
    if (selectedState && prevStateRef.current !== selectedState && prevStateRef.current !== null) {
      setSelectedDistrict(null);
    }
    prevStateRef.current = selectedState;
  }, [selectedState]);

  // Drop district when browsing Senate or President (district is House-only)
  useEffect(() => {
    if (
      selectedAccountTypeName === "candidate"
      && !shouldShowDistrictForCandidateOffice(selectedCandidateOffice)
      && selectedDistrict
    ) {
      setSelectedDistrict(null);
    }
  }, [selectedAccountTypeName, selectedCandidateOffice, selectedDistrict]);

  // If President is hidden for this cycle, fall back to House
  useEffect(() => {
    const normalized = normalizeCandidateOffice(selectedCandidateOffice);
    if (normalized !== selectedCandidateOffice) {
      setSelectedCandidateOffice(normalized);
    }
  }, [selectedCandidateOffice, visibleCandidateOfficeOptions]);

  // Reset page when filters change; clear stale results so the UI shows loading immediately
  useEffect(() => {
    setPage(1);
    allCongressResultsRef.current = [];
    setResults([]);
    setError(null);
    const skipCandidateFetch =
      selectedAccountTypeName === "candidate"
      && shouldSuggestStateFilterForCandidates(selectedCandidateOffice)
      && !selectedState;
    setLoading(!skipCandidateFetch);
  }, [debouncedQuery, selectedAccountTypeName, selectedState, selectedDistrict, selectedCandidateOffice, civixAccountOnly]);

  // Main data fetch
  const fetchData = useCallback(async () => {
    if (loadingRef.current) return;

    if (
      selectedAccountTypeName === "candidate"
      && shouldSuggestStateFilterForCandidates(selectedCandidateOffice)
      && !selectedState
    ) {
      setResults([]);
      setTotalPages(1);
      setTotalCount(0);
      setLoading(false);
      setError(null);
      return;
    }

    loadingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      let districtFilter = normalizeDistrictForApi(selectedDistrict);
      if (selectedState && STATE_DISTRICT_MAP[selectedState] === 1) districtFilter = null;

      let newResults = [];
      let pages = 1;
      let count = 0;

      if (selectedAccountTypeName === "candidate") {
        const fecParams = buildFECCandidateListParams({
          office: selectedCandidateOffice,
          state: selectedState,
          district: districtFilter,
          query: debouncedQuery || undefined,
          page,
          pageSize: PAGE_SIZE,
        });

        const response = await fetchFECCandidates(fecParams);

        let mapped = (response.results || []).map(mapFECCandidateToDisplay);
        if (civixAccountOnly) mapped = mapped.filter((c) => c.hasCivixThreadAccount);
        newResults = mapped.map((c) => ({ ...c, _type: "candidate" }));
        pages = response.total_pages || 1;
        count = response.count || 0;

      } else if (selectedAccountTypeName === "senator" || selectedAccountTypeName === "representative") {
        const position = selectedAccountTypeName === "senator" ? "senate" : "house";

        // Backend returns all congress members in one response (no server-side pagination).
        // Fetch the full list only on page 1, then paginate client-side.
        if (page === 1) {
          const response = await searchCongressMembers({
            query: debouncedQuery || "",
            state: selectedState || null,
            district: districtFilter,
            position,
            page: 1, pageSize: 9999,
          });

          let mapped = (response.results || []).map((m) => mapCongressMemberToSummary(m, position));
          if (civixAccountOnly) mapped = mapped.filter((m) => m.hasCivixThreadAccount);
          allCongressResultsRef.current = mapped.map((u) => ({ ...u, _type: "user" }));
        }

        const allItems = allCongressResultsRef.current;
        count = allItems.length;
        pages = Math.max(1, Math.ceil(count / PAGE_SIZE));
        const startIdx = (page - 1) * PAGE_SIZE;
        newResults = allItems.slice(startIdx, startIdx + PAGE_SIZE);

      } else {
        const response = await searchUsers(debouncedQuery, page, PAGE_SIZE, selectedAccountTypeName, selectedState, districtFilter);
        let mapped = (response.results || []).map(mapApiUserToSummary);
        mapped.sort((a, b) => b.followerCount - a.followerCount);
        newResults = mapped.map((u) => ({ ...u, _type: "user" }));
        pages = response.total_pages || 1;
        count = response.count || 0;
      }

      setResults(newResults);
      setTotalPages(pages);
      setTotalCount(count);
    } catch (err) {
      const msg = err.response?.data?.error || err.message || "Failed to load data";
      setError(msg);
      setResults([]);
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [debouncedQuery, selectedAccountTypeName, selectedState, selectedDistrict, selectedCandidateOffice, civixAccountOnly, page]);

  useEffect(() => {
    loadingRef.current = false;
    fetchData();
  }, [fetchData]);

  const handleFollowToggle = async (userId, shouldFollow) => {
    if (!isLoggedIn) return;
    try {
      if (shouldFollow) await followUser(userId);
      else await unfollowUser(userId);
    } catch (err) {
      setError("Failed to update follow status.");
    }
  };

  const handleCardClick = (item) => {
    if (item._type === "candidate") {
      navigate(`/candidates/${item.id}`, { state: { totalRaised: item.totalRaised } });
      return;
    }
    // Match mobile: only navigate to congress detail when we have a real bioguide_id (never use item.id — can be DB pk and cause 500)
    const isCongress = item.accountType === "senator" || item.accountType === "representative";
    const bioguideId = item.bioguideId ?? item.bioguide_id;
    if (isCongress && bioguideId && String(bioguideId).trim()) {
      navigate(`/congress/${bioguideId}`);
      return;
    }
    const profileId = item.civixThreadUserId || item.id;
    if (profileId) {
      navigate(`/profile/${profileId}`);
    } else {
      navigate(`/dashboard`);
    }
  };

  const handleFilterSelect = (option) => {
    setActiveFilter(option.id);
    setSelectedAccountTypeName(option.accountTypeName);
    if (option.accountTypeName === "candidate") {
      setSelectedCandidateOffice(DEFAULT_CANDIDATE_OFFICE);
    }
    if (option.accountTypeName === "senator") {
      setSelectedDistrict(null);
    } else if (!["candidate", "senator", "representative", CIVIX_USER_ACCOUNT_TYPE].includes(option.accountTypeName)) {
      setSelectedState(null);
      setSelectedDistrict(null);
      setCivixAccountOnly(false);
    }
    setShowFilterModal(false);
  };

  const handleCandidateOfficeChange = (officeCode) => {
    setSelectedCandidateOffice(normalizeCandidateOffice(officeCode));
  };

  const showLocationFilters = ["candidate", "senator", "representative", CIVIX_USER_ACCOUNT_TYPE]
    .includes(selectedAccountTypeName);
  const showAccountFilter = ["candidate", "senator", "representative"].includes(selectedAccountTypeName);
  const showDistrictFilter = showLocationFilters && selectedState && STATE_DISTRICT_MAP[selectedState] > 1 && (
    selectedAccountTypeName === "representative"
    || (isCandidateBrowse && shouldShowDistrictForCandidateOffice(selectedCandidateOffice))
    || selectedAccountTypeName === CIVIX_USER_ACCOUNT_TYPE
  );
  const showCandidateStateTip = isCandidateBrowse
    && !selectedState
    && shouldSuggestStateFilterForCandidates(selectedCandidateOffice);
  const activeLabel = FILTER_OPTIONS.find((f) => f.id === activeFilter)?.label || "All Types";
  const searchPlaceholder = getDiscoverSearchPlaceholder(selectedAccountTypeName);
  const districtOptions = selectedState ? getDistrictOptions(selectedState) : [];

  const getEmptyIcon = () => {
    switch (selectedAccountTypeName) {
      case "candidate": return <HowToVoteIcon sx={{ fontSize: 48, color: theme.primary }} />;
      case "senator": return <AccountBalanceIcon sx={{ fontSize: 48, color: theme.primary }} />;
      case "representative": return <GroupsIcon sx={{ fontSize: 48, color: theme.primary }} />;
      default: return <PersonIcon sx={{ fontSize: 48, color: theme.primary }} />;
    }
  };

  const getEmptyTitle = () => {
    if (searchQuery) {
      switch (selectedAccountTypeName) {
        case "candidate": return "No candidates found";
        case "senator": return "No senators found";
        case "representative": return "No representatives found";
        default: return "No results found";
      }
    }
    if (candidateLocationRequired && !selectedState) return "Select a state";
    switch (selectedAccountTypeName) {
      case "candidate": return "Explore Candidates";
      case "senator": return "Find Your Senators";
      case "representative": return "Find Representatives";
      default: return "Discover People";
    }
  };

  const getEmptySubtext = () => {
    if (searchQuery) return "Try adjusting your search terms or filters";
    switch (selectedAccountTypeName) {
      case "candidate":
        return candidateLocationRequired && !selectedState
          ? "Location is required to browse House and Senate candidates."
          : `Search for ${getCandidateOfficeLabel(selectedCandidateOffice)} candidates running for federal office.`;
      case "senator": return "Find senators representing your state";
      case "representative": return "Search for representatives in your district";
      default: return "Find people and organizations on CivixThread";
    }
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f8fafc", p: 2 }}>
      <Container maxWidth="md" sx={{ px: 0 }}>
        <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", overflow: "hidden" }}>
        {/* Find | Leaderboard tabs */}
        <Box
          sx={{
            display: "flex",
            gap: 0,
            borderBottom: "1px solid #e5e7eb",
            px: { xs: 1.5, sm: 2 },
          }}
        >
          {[
            { id: "find", label: "Find" },
            { id: "leaderboard", label: "Leaderboard" },
          ].map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <Box
                key={tab.id}
                component="button"
                onClick={() => setActiveTab(tab.id)}
                sx={{
                  minHeight: 44,
                  px: 2.5,
                  py: 1.25,
                  border: "none",
                  borderBottom: isActive ? `2px solid ${theme.primary}` : "2px solid transparent",
                  mb: "-1px",
                  bgcolor: "transparent",
                  color: isActive ? theme.primary : "#64748b",
                  fontWeight: 700,
                  fontSize: 14,
                  cursor: "pointer",
                  fontFamily: "inherit",
                  transition: "color 0.15s",
                  "&:hover": { color: theme.primary },
                }}
              >
                {tab.label}
              </Box>
            );
          })}
        </Box>

        {activeTab === "leaderboard" ? (
          <CallsLeaderboard isLoggedIn={isLoggedIn} />
        ) : (
          <>
            {/* Search + filters */}
            <Box sx={{ py: 2, px: { xs: 1.5, sm: 2 }, borderBottom: "1px solid #e5e7eb" }}>
          {/* Search bar */}
          <Box sx={{ position: "relative", mb: 1.5 }}>
            <SearchIcon sx={{
              position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)",
              color: "#94a3b8", fontSize: 20, pointerEvents: "none",
            }} />
            <input
              type="text"
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              style={{
                width: "100%", boxSizing: "border-box",
                paddingLeft: 44, paddingRight: searchQuery ? 40 : 16,
                paddingTop: 11, paddingBottom: 11,
                border: "1.5px solid #e2e8f0", borderRadius: 10,
                fontSize: 14, fontFamily: "inherit", color: "#0f172a",
                background: "#f8fafc", outline: "none",
                transition: "border-color 0.15s",
              }}
              onFocus={(e) => { e.target.style.borderColor = "#047857"; e.target.style.background = "#fff"; }}
              onBlur={(e) => { e.target.style.borderColor = "#e2e8f0"; e.target.style.background = "#f8fafc"; }}
            />
            {searchQuery && (
              <IconButton
                size="small"
                onClick={() => setSearchQuery("")}
                sx={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }}
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          {/* Filter toolbar */}
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          {/* Type picker */}
          <Button
            variant="contained"
            startIcon={<FilterListIcon sx={{ fontSize: "17px !important" }} />}
            onClick={() => setShowFilterModal(true)}
            sx={{
              textTransform: "none", fontWeight: 700, borderRadius: "20px",
              fontSize: 13, height: 36, px: 2,
              bgcolor: theme.primary, color: "#fff", boxShadow: "none",
              "&:hover": { bgcolor: theme.primaryHover, boxShadow: "none" },
            }}
          >
            {activeLabel}
          </Button>

          {isCandidateBrowse && (
            <CandidateOfficeFilter
              options={visibleCandidateOfficeOptions}
              value={selectedCandidateOffice}
              onChange={handleCandidateOfficeChange}
              compact
            />
          )}

          {/* State picker */}
          {showLocationFilters && (
            <Autocomplete
              id="discover-state-filter"
              size="small"
              options={candidateLocationRequired ? US_STATES : STATE_OPTIONS}
              getOptionLabel={(opt) => opt.name}
              isOptionEqualToValue={(opt, val) => opt.code === val.code}
              value={
                selectedState
                  ? (US_STATES.find((s) => s.code === selectedState) ?? STATE_OPTIONS.find((s) => s.code === selectedState) ?? null)
                  : (candidateLocationRequired ? null : STATE_OPTIONS[0])
              }
              onChange={(_, newValue) => {
                const code = newValue?.code || null;
                if (candidateLocationRequired && !code) return;
                setSelectedState(code);
              }}
              disableClearable={candidateLocationRequired && !!selectedState}
              sx={{ width: 180 }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={candidateLocationRequired && !selectedState ? "Select a state" : undefined}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <LocationOnIcon sx={{ fontSize: 15, mr: 0.5, color: selectedState ? "#fff" : "#64748b", flexShrink: 0 }} />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "20px", fontWeight: 600, fontSize: 13, height: 36,
                      backgroundColor: selectedState ? theme.primary : "#fff",
                      color: selectedState ? "#fff" : "#0f172a",
                      border: selectedState ? `1.5px solid ${theme.primary}` : "1.5px solid #e2e8f0",
                      "& fieldset": { border: "none" },
                      "& .MuiAutocomplete-input": { color: selectedState ? "#fff" : "#0f172a", p: 0, fontSize: 13 },
                      "& .MuiSvgIcon-root": { color: selectedState ? "#fff" : "#94a3b8", fontSize: 18 },
                    },
                  }}
                />
              )}
              ListboxProps={{ sx: { maxHeight: 320, "& .MuiAutocomplete-option": { fontSize: 14, py: 1 } } }}
              filterOptions={(options, { inputValue }) => {
                const q = inputValue.trim().toLowerCase();
                if (!q) return options;
                return options.filter((opt) => opt.name.toLowerCase().includes(q) || (opt.code && opt.code.toLowerCase().includes(q)));
              }}
              noOptionsText="No matching state"
            />
          )}

          {/* District picker */}
          {showDistrictFilter && (
            <FormControl size="small" sx={{ minWidth: 150 }}>
              <Select
                value={selectedDistrict || ""}
                displayEmpty
                onChange={(e) => setSelectedDistrict(e.target.value || null)}
                sx={{
                  borderRadius: "20px", fontWeight: 600, fontSize: 13, height: 36,
                  bgcolor: selectedDistrict ? theme.primary : "#fff",
                  color: selectedDistrict ? "#fff" : "#0f172a",
                  border: selectedDistrict ? `1.5px solid ${theme.primary}` : "1.5px solid #e2e8f0",
                  "& .MuiSelect-icon": { color: selectedDistrict ? "#fff" : "#94a3b8" },
                  "& fieldset": { border: "none" },
                  "& .MuiSelect-select": { display: "flex", alignItems: "center", gap: 0.5 },
                }}
              >
                <MenuItem value="">All Districts</MenuItem>
                {districtOptions.map((d) => <MenuItem key={d.value} value={d.value}>{d.label}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          {/* Has Account chip */}
          {showAccountFilter && (
            <Box
              component="button"
              onClick={() => setCivixAccountOnly((v) => !v)}
              sx={{
                display: "flex", alignItems: "center", gap: 0.75,
                height: 36, px: 2, borderRadius: "20px", border: "1.5px solid",
                borderColor: civixAccountOnly ? theme.primary : "#e2e8f0",
                bgcolor: civixAccountOnly ? "#f0fdf4" : "#fff",
                color: civixAccountOnly ? theme.primary : "#64748b",
                fontWeight: 600, fontSize: 13, cursor: "pointer",
                fontFamily: "inherit", transition: "all 0.15s",
                "&:hover": { borderColor: theme.primary, color: theme.primary },
              }}
            >
              Has Account
            </Box>
          )}

          {/* Clear filters */}
          {((!candidateLocationRequired && selectedState) || selectedDistrict || civixAccountOnly) && (
            <Box
              component="button"
              onClick={() => {
                if (!candidateLocationRequired) setSelectedState(null);
                setSelectedDistrict(null);
                setCivixAccountOnly(false);
              }}
              sx={{
                display: "flex", alignItems: "center", gap: 0.5,
                height: 36, px: 1.75, borderRadius: "20px",
                border: "1.5px solid #e2e8f0", bgcolor: "#fff",
                color: "#64748b", fontWeight: 600, fontSize: 13,
                cursor: "pointer", fontFamily: "inherit",
                boxShadow: "inset 0 1px 3px rgba(15,23,42,0.06)",
                "&:hover": { borderColor: "#cbd5e1", color: "#334155" },
              }}
            >
              <ClearIcon sx={{ fontSize: 14 }} />
              Clear
            </Box>
          )}
            </Box>
            </Box>

            {/* Tip banner for House/Senate candidates without state */}
            {showCandidateStateTip && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: { xs: 1.5, sm: 2 }, py: 1.5, bgcolor: "#f0fdf4", borderBottom: "1px solid #bbf7d0" }}>
                <LightbulbOutlinedIcon sx={{ fontSize: 16, color: theme.primary }} />
                <Typography sx={{ fontSize: 13, color: "#065f46" }}>
                  Location is required to browse House and Senate candidates.
                </Typography>
              </Box>
            )}

            {/* Results */}
            <Box>
          {loading ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
              <CircularProgress sx={{ color: theme.primary, mb: 2 }} />
              <Typography sx={{ fontWeight: 600, color: "#334155" }}>
                {selectedAccountTypeName === "candidate"
                  ? "Loading candidates..."
                  : selectedAccountTypeName === "senator"
                    ? "Loading senators..."
                    : selectedAccountTypeName === "representative"
                      ? "Loading representatives..."
                      : "Searching..."}
              </Typography>
              {showCandidateStateTip && (
                <Typography sx={{ fontSize: 13, color: "#64748b", mt: 1, textAlign: "center" }}>
                  Select a state to load candidates.
                </Typography>
              )}
            </Box>
          ) : error ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
              <Box sx={{ width: 72, height: 72, borderRadius: "50%", bgcolor: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center", mb: 2 }}>
                <Typography sx={{ fontSize: 40 }}>!</Typography>
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 18, color: "#0f172a", mb: 1 }}>Something went wrong</Typography>
              <Typography sx={{ fontSize: 14, color: "#64748b", mb: 3, textAlign: "center", maxWidth: 320 }}>{error}</Typography>
              <Button variant="contained" startIcon={<RefreshIcon />} onClick={fetchData}
                sx={{ borderRadius: "20px", fontWeight: 600, textTransform: "none", background: theme.gradient, "&:hover": { opacity: 0.9, background: theme.gradient } }}>
                Try Again
              </Button>
            </Box>
          ) : results.length === 0 ? (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", py: 8 }}>
              <Box sx={{ width: 80, height: 80, borderRadius: "50%", bgcolor: "#f1f5f9", display: "flex", alignItems: "center", justifyContent: "center", mb: 2.5 }}>
                {getEmptyIcon()}
              </Box>
              <Typography sx={{ fontWeight: 700, fontSize: 20, color: "#0f172a", mb: 1 }}>{getEmptyTitle()}</Typography>
              <Typography sx={{ fontSize: 15, color: "#64748b", textAlign: "center", maxWidth: 300, lineHeight: 1.5 }}>{getEmptySubtext()}</Typography>
              {showCandidateStateTip && (
                <Button variant="contained" startIcon={<LocationOnIcon />}
                  onClick={() => document.getElementById("discover-state-filter")?.focus()}
                  sx={{ mt: 3, borderRadius: "20px", fontWeight: 600, textTransform: "none", background: theme.gradient, "&:hover": { opacity: 0.9, background: theme.gradient } }}>
                  Select a State
                </Button>
              )}
            </Box>
          ) : (
            <>
              {results.map((item) =>
                item._type === "candidate" ? (
                  <CandidateResultCard
                    key={item.id}
                    candidate={item}
                    onCardClick={handleCardClick}
                    onFollowToggle={handleFollowToggle}
                    isLoggedIn={isLoggedIn}
                  />
                ) : (
                  <UserResultCard
                    key={item.id}
                    user={item}
                    onFollowToggle={handleFollowToggle}
                    isLoggedIn={isLoggedIn}
                    onCardClick={handleCardClick}
                    showAccountTypeBadge={selectedAccountTypeName === CIVIX_USER_ACCOUNT_TYPE}
                  />
                )
              )}
              {totalPages > 1 && (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, p: 2.5, borderTop: "1px solid #f1f5f9" }}>
                  <IconButton
                    size="small"
                    disabled={page <= 1}
                    onClick={() => { setPage(p => Math.max(1, p - 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    sx={{
                      width: 36, height: 36, borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      color: page <= 1 ? "#cbd5e1" : "#334155",
                      "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
                    }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 20 }} />
                  </IconButton>

                  {(() => {
                    const pages = [];
                    const maxVisible = 5;
                    let start = Math.max(1, page - Math.floor(maxVisible / 2));
                    let end = Math.min(totalPages, start + maxVisible - 1);
                    if (end - start + 1 < maxVisible) start = Math.max(1, end - maxVisible + 1);

                    if (start > 1) {
                      pages.push(
                        <Button key={1} size="small" onClick={() => { setPage(1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                          sx={{ minWidth: 36, height: 36, borderRadius: "8px", fontWeight: 600, fontSize: 14,
                            color: "#334155", bgcolor: "#fff", border: "1px solid #e2e8f0",
                            "&:hover": { bgcolor: "#f8fafc" } }}>
                          1
                        </Button>
                      );
                      if (start > 2) pages.push(<Typography key="start-dots" sx={{ px: 0.5, color: "#94a3b8", fontSize: 14 }}>...</Typography>);
                    }

                    for (let i = start; i <= end; i++) {
                      pages.push(
                        <Button key={i} size="small" onClick={() => { setPage(i); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                          sx={{ minWidth: 36, height: 36, borderRadius: "8px", fontWeight: 600, fontSize: 14,
                            color: i === page ? "#fff" : "#334155",
                            bgcolor: i === page ? theme.primary : "#fff",
                            border: i === page ? "none" : "1px solid #e2e8f0",
                            "&:hover": { bgcolor: i === page ? theme.primary : "#f8fafc", opacity: i === page ? 0.9 : 1 } }}>
                          {i}
                        </Button>
                      );
                    }

                    if (end < totalPages) {
                      if (end < totalPages - 1) pages.push(<Typography key="end-dots" sx={{ px: 0.5, color: "#94a3b8", fontSize: 14 }}>...</Typography>);
                      pages.push(
                        <Button key={totalPages} size="small" onClick={() => { setPage(totalPages); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                          sx={{ minWidth: 36, height: 36, borderRadius: "8px", fontWeight: 600, fontSize: 14,
                            color: "#334155", bgcolor: "#fff", border: "1px solid #e2e8f0",
                            "&:hover": { bgcolor: "#f8fafc" } }}>
                          {totalPages}
                        </Button>
                      );
                    }

                    return pages;
                  })()}

                  <IconButton
                    size="small"
                    disabled={page >= totalPages}
                    onClick={() => { setPage(p => Math.min(totalPages, p + 1)); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    sx={{
                      width: 36, height: 36, borderRadius: "8px",
                      border: "1px solid #e2e8f0",
                      color: page >= totalPages ? "#cbd5e1" : "#334155",
                      "&:hover": { bgcolor: "#f8fafc", borderColor: "#cbd5e1" },
                    }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 20 }} />
                  </IconButton>

                  <Typography sx={{ ml: 1.5, fontSize: 13, color: "#64748b" }}>
                    Page {page} of {totalPages}{totalCount ? ` (${totalCount.toLocaleString()} results)` : ""}
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
          </>
        )}
        </Box>
      </Container>

      {/* Filter Type Modal */}
      <Dialog open={showFilterModal} onClose={() => setShowFilterModal(false)} maxWidth="xs" fullWidth
        PaperProps={{ sx: { borderRadius: "16px" } }}>
        <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 700, fontSize: 20 }}>
          Filter by Type
          <IconButton onClick={() => setShowFilterModal(false)} sx={{ bgcolor: "#f1f5f9", width: 36, height: 36 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {VISIBLE_FILTER_OPTIONS.map((option) => {
            const requiresLogin = LOGIN_REQUIRED_FILTERS.includes(option.id);
            const isDisabled = !isLoggedIn && requiresLogin;
            const isSelected = activeFilter === option.id;
            return (
              <Box
                key={option.id}
                onClick={() => !isDisabled && handleFilterSelect(option)}
                sx={{
                  display: "flex", justifyContent: "space-between", alignItems: "center",
                  px: 3, py: 2, cursor: isDisabled ? "not-allowed" : "pointer",
                  borderBottom: "1px solid #f1f5f9",
                  bgcolor: isSelected ? "#f0fdf4" : isDisabled ? "#f8fafc" : "#fff",
                  "&:hover": !isDisabled ? { bgcolor: isSelected ? "#f0fdf4" : "#f8fafc" } : {},
                }}
              >
                <Box>
                  <Typography sx={{ fontWeight: isSelected ? 700 : 500, fontSize: 16, color: isDisabled ? "#475569" : "#0f172a" }}>
                    {option.label}
                  </Typography>
                  {isDisabled && (
                    <Typography sx={{ fontSize: 12, color: "#475569", fontStyle: "italic", mt: 0.5 }}>
                      Sign in to filter by this type
                    </Typography>
                  )}
                </Box>
                {isSelected && !isDisabled && <CheckCircleIcon sx={{ fontSize: 20, color: theme.primary }} />}
                {isDisabled && <LockIcon sx={{ fontSize: 16, color: "#475569" }} />}
              </Box>
            );
          })}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
